import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IconCalendarPlus, IconCheck, IconArrowLeft } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useI18n } from "@/contexts/I18nContext";

export default function PublicBooking() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/public/clinician/${slug}`);
      setClinic(data);
      if (data.services?.length && !service) setService(data.services[0]);
    } catch (e) { setError(formatApiError(e)); }
  })(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => { (async () => {
    if (!service || !date) return;
    setSlots([]); setSelectedSlot(null);
    try {
      const { data } = await api.get(`/public/clinician/${slug}/slots?service_id=${service.id}&date=${date}`);
      setSlots(data.slots || []);
    } catch { /* ignore */ }
  })(); }, [slug, service, date]);

  const book = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post(`/public/clinician/${slug}/book`, {
        service_id: service.id,
        start: selectedSlot.start,
        ...form,
      });
      setConfirmed(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (!clinic) {
    return (
      <div className="bg-paper min-h-screen">
        <SiteNav />
        <div className="wrap py-20 text-center text-tert">{error || "Cargando…"}</div>
        <SiteFooter />
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="bg-paper min-h-screen">
        <SiteNav />
        <div className="wrap max-w-[600px] py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-oktext text-white mx-auto flex items-center justify-center">
            <IconCheck size={26} />
          </div>
          <h1 className="font-display text-[26px] font-medium mt-4">
            {lang === "es" ? "¡Tu cita fue reservada!" : "Your appointment is booked!"}
          </h1>
          <p className="text-muted mt-3">
            {lang === "es"
              ? `Nos vemos el ${new Date(confirmed.start).toLocaleString("es-HN")}. Recibirás una confirmación por correo.`
              : `See you on ${new Date(confirmed.start).toLocaleString()}`}
          </p>
          <button className="btn mt-6" onClick={() => nav("/")}>{lang === "es" ? "Volver al sitio" : "Back to site"}</button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-screen">
      <SiteNav />
      <section className="py-16">
        <div className="wrap max-w-[720px]">
          <p className="eyebrow">{lang === "es" ? "Agenda pública" : "Public booking"}</p>
          <h1 className="font-display text-[28px] font-medium mt-1">{clinic.clinician_name}</h1>
          {clinic.bio && <p className="text-muted text-[14.5px] mt-2">{clinic.bio}</p>}

          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            <div className="field"><label>{lang === "es" ? "Servicio" : "Service"}</label>
              <select className="select-css" value={service?.id || ""} onChange={(e) => setService(clinic.services.find((s) => s.id === e.target.value))} data-testid="pb-service">
                {clinic.services.map((s) => <option key={s.id} value={s.id}>{s.name} · L{s.price_hnl} · {s.duration_min} min</option>)}
              </select>
            </div>
            <div className="field"><label>{lang === "es" ? "Fecha" : "Date"}</label>
              <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pb-date" />
            </div>
          </div>

          <p className="lbl">{lang === "es" ? "Horarios disponibles" : "Available slots"}</p>
          {slots.length === 0 ? (
            <p className="text-tert text-[13px]">{lang === "es" ? "No hay horarios disponibles ese día." : "No slots for that day."}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((sl) => (
                <button key={sl.start} className={`chip ${selectedSlot?.start === sl.start ? "sel" : ""}`} onClick={() => setSelectedSlot(sl)} data-testid={`pb-slot-${sl.label}`}>
                  {sl.label}
                </button>
              ))}
            </div>
          )}

          {selectedSlot && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card mt-6">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="field"><label>{lang === "es" ? "Nombre" : "Name"}</label><input className="input" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="pb-name" /></div>
                <div className="field"><label>Email</label><input className="input" type="email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} data-testid="pb-email" /></div>
                <div className="field"><label>{lang === "es" ? "Teléfono" : "Phone"}</label><input className="input" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} data-testid="pb-phone" /></div>
                <div className="field sm:col-span-2"><label>{lang === "es" ? "Nota (opcional)" : "Note (optional)"}</label><textarea className="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              {error && <p className="text-errtext text-[13px]">{error}</p>}
              <button className="btn mt-2" onClick={book} disabled={loading || !form.patient_name || !form.patient_email} data-testid="pb-confirm">
                <IconCalendarPlus size={13} /> {loading ? "..." : (lang === "es" ? "Confirmar cita" : "Confirm")}
              </button>
            </motion.div>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
